import  {useState, useEffect} from "react";
import {useNavigate} from "react-router-dom";
import { getShops } from "../utils/api";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import Toast from "../components/Toast";


const API = axios.create({ baseURL: process.env.REACT_APP_API_URL || "http://localhost:5000/api" });
API.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});


export default function ManageCoupons() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [shop, setShop] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true); // ✅ covers BOTH shop + coupons
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [form, setForm] = useState({
    code: "", discount_type: "percentage",
    discount_value: "", min_amount: "",
    max_uses: "100", valid_till: ""
  });
  const [toast, setToast] = useState({ message: "", type: "success" });
  const showToast = (message, type = "success") => setToast({ message, type });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await getShops({});
        const myShop = res.data.find((s) => Number(s.owner_id) === Number(user.id));
        if (myShop) {
          setShop(myShop);
          // ✅ Fetch coupons BEFORE setting loading to false
          try {
            const couponRes = await API.get(`/coupons/shop/${myShop.id}`);
            setCoupons(couponRes.data);
          } catch (err) { console.error(err); }
        }
      } catch (err) { setError("Could not load shop."); }
      finally { setLoading(false); } // ✅ Only false AFTER everything is loaded
    };
    fetchData();
  }, [user.id]);

  const fetchCoupons = async (shopId) => {
    try {
      const res = await API.get(`/coupons/shop/${shopId}`);
      setCoupons(res.data);
    } catch (err) { console.error(err); }
  };

  const handleCreate = async () => {
    if (!form.code || !form.discount_value || !form.valid_till) {
      setError("Code, discount value and valid till are required.");
      return;
    }
    setError("");
    try {
      await API.post("/coupons", { ...form, shop_id: shop.id });
      setSuccess("Coupon created! 🎉");
      setShowForm(false);
      setForm({ code: "", discount_type: "percentage", discount_value: "", min_amount: "", max_uses: "100", valid_till: "" });
      fetchCoupons(shop.id);
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) { setError(err.response?.data?.message || "Could not create coupon."); }
  };

  const handleToggle = async (id) => {
    try { await API.patch(`/coupons/${id}/toggle`); fetchCoupons(shop.id); }
    catch (err) { setError("Could not update coupon."); }
  };

  const handleDelete = async (id) => {
  try {
    await API.delete(`/coupons/${id}`);
    fetchCoupons(shop.id);
    showToast("Coupon deleted! ✅");
  } catch (err) { showToast("Could not delete coupon.", "error"); }
};

  const isExpired = (date) => new Date(date) < new Date();

  // ✅ Single loading screen covers everything — no flash!
  if (loading) return (
    <div style={styles.center}>
      <p style={{ fontSize: "32px" }}>🎟️</p>
      <p style={{ color: "var(--text-muted)", marginTop: "12px" }}>Loading coupons...</p>
    </div>
  );

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div style={styles.headerInner}>
          <div>
            <h1 style={styles.headerTitle}>🎟️ Coupons</h1>
            <p style={styles.headerSub}>Create discounts for your customers</p>
          </div>
          <div style={styles.headerBtns}>
            <button onClick={() => navigate("/dashboard")} style={styles.backBtn}>← Dashboard</button>
            <button onClick={() => { setShowForm(!showForm); setError(""); }} style={styles.createBtn}>
              {showForm ? "✕ Cancel" : "+ Create Coupon"}
            </button>
          </div>
        </div>
      </div>

      <div style={styles.container}>
        {error && <div style={styles.errorBox}>{error}</div>}
        {success && <div style={styles.successBox}>{success}</div>}

        {/* Create Form */}
        {showForm && (
          <div style={styles.formCard}>
            <h3 style={styles.formTitle}>🎟️ New Coupon</h3>
            <div style={styles.formGrid}>
              <div>
                <label style={styles.label}>Coupon Code *</label>
                <input placeholder="e.g. SAVE50" value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                  style={styles.input} maxLength={20} />
              </div>
              <div>
                <label style={styles.label}>Discount Type *</label>
                <select value={form.discount_type}
                  onChange={(e) => setForm({ ...form, discount_type: e.target.value })}
                  style={styles.input}>
                  <option value="percentage">Percentage (%) off</option>
                  <option value="fixed">Fixed (₹) off</option>
                </select>
              </div>
              <div>
                <label style={styles.label}>Value * {form.discount_type === "percentage" ? "(%)" : "(₹)"}</label>
                <input type="number"
                  placeholder={form.discount_type === "percentage" ? "e.g. 20" : "e.g. 50"}
                  value={form.discount_value}
                  onChange={(e) => setForm({ ...form, discount_value: e.target.value })}
                  style={styles.input} min="1" />
              </div>
              <div>
                <label style={styles.label}>Min Amount (₹)</label>
                <input type="number" placeholder="Optional" value={form.min_amount}
                  onChange={(e) => setForm({ ...form, min_amount: e.target.value })}
                  style={styles.input} />
              </div>
              <div>
                <label style={styles.label}>Max Uses</label>
                <input type="number" value={form.max_uses}
                  onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
                  style={styles.input} />
              </div>
              <div>
                <label style={styles.label}>Valid Till *</label>
                <input type="date" value={form.valid_till}
                  onChange={(e) => setForm({ ...form, valid_till: e.target.value })}
                  style={styles.input} min={new Date().toISOString().split("T")[0]} />
              </div>
            </div>
            {form.code && form.discount_value && (
              <div style={styles.preview}>
                <span style={styles.previewCode}>{form.code}</span>
                <span style={styles.previewDiscount}>
                  {form.discount_type === "percentage"
                    ? `${form.discount_value}% OFF`
                    : `₹${form.discount_value} OFF`}
                </span>
              </div>
            )}
            <button onClick={handleCreate} style={styles.submitBtn}>🎟️ Create Coupon</button>
          </div>
        )}

        {/* Stats */}
        <div style={styles.statsRow}>
          <div style={styles.statCard}>
            <p style={styles.statNum}>{coupons.length}</p>
            <p style={styles.statLabel}>Total</p>
          </div>
          <div style={styles.statCard}>
            <p style={{ ...styles.statNum, color: "#4caf50" }}>
              {coupons.filter((c) => c.is_active && !isExpired(c.valid_till)).length}
            </p>
            <p style={styles.statLabel}>Active</p>
          </div>
          <div style={styles.statCard}>
            <p style={{ ...styles.statNum, color: "var(--accent)" }}>
              {coupons.reduce((sum, c) => sum + (c.used_count || 0), 0)}
            </p>
            <p style={styles.statLabel}>Total Uses</p>
          </div>
        </div>

        {/* Coupons List */}
        {coupons.length === 0 ? (
          <div style={styles.emptyState}>
            <p style={{ fontSize: "48px", marginBottom: "12px" }}>🎟️</p>
            <p style={{ color: "white", fontWeight: "bold", marginBottom: "6px" }}>No coupons yet!</p>
            <p style={{ color: "var(--text-muted)", marginBottom: "20px" }}>Create your first coupon</p>
            <button onClick={() => setShowForm(true)} style={styles.submitBtn}>+ Create Coupon</button>
          </div>
        ) : (
          coupons.map((coupon) => (
            <div key={coupon.id}
              style={{ ...styles.couponCard, opacity: isExpired(coupon.valid_till) ? 0.6 : 1 }}>
              <div style={styles.couponLeft}>
                <div style={styles.codeBadge}>
                  <p style={styles.codeText}>{coupon.code}</p>
                  <p style={styles.discountText}>
                    {coupon.discount_type === "percentage"
                      ? `${coupon.discount_value}%`
                      : `₹${coupon.discount_value}`} OFF
                  </p>
                </div>
                <div>
                  <p style={styles.couponMeta}>
                    📅 Till {new Date(coupon.valid_till).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                  </p>
                  <p style={styles.couponMeta}>🔢 {coupon.used_count}/{coupon.max_uses} used</p>
                </div>
              </div>
              <div style={styles.couponRight}>
                <span style={{
                  ...styles.statusBadge,
                  color: isExpired(coupon.valid_till) ? "var(--text-muted)" : coupon.is_active ? "#4caf50" : "#ff9800",
                  background: isExpired(coupon.valid_till) ? "var(--bg-secondary)" : coupon.is_active ? "rgba(76,175,80,0.1)" : "rgba(255,152,0,0.1)",
                }}>
                  {isExpired(coupon.valid_till) ? "⌛ Expired" : coupon.is_active ? "✅ Active" : "⏸️ Paused"}
                </span>
                <div style={{ display: "flex", gap: "6px" }}>
                  {!isExpired(coupon.valid_till) && (
                    <button onClick={() => handleToggle(coupon.id)} style={styles.toggleBtn}>
                      {coupon.is_active ? "Pause" : "Activate"}
                    </button>
                  )}
                  <button onClick={() => handleDelete(coupon.id)} style={styles.deleteBtn}>🗑️</button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
      <Toast message={toast.message} type={toast.type} onClose={() => setToast({ message: "" })} />
    </div>
  );
}

const styles = {
  page: { minHeight: "100vh", background: "var(--bg-primary)" },
  center: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "60vh" },
  header: { background: "var(--bg-secondary)", borderBottom: "1px solid var(--border)", padding: "16px 20px" },
  headerInner: { maxWidth: "800px", margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" },
  headerTitle: { color: "white", margin: "0 0 2px", fontSize: "1.4rem", fontWeight: "800" },
  headerSub: { color: "var(--text-muted)", margin: 0, fontSize: "13px" },
  headerBtns: { display: "flex", gap: "8px", flexWrap: "wrap" },
  backBtn: { background: "var(--bg-card)", border: "1px solid var(--border)", color: "var(--text-secondary)", padding: "8px 14px", borderRadius: "8px", cursor: "pointer", fontSize: "13px" },
  createBtn: { background: "var(--accent)", border: "none", color: "white", padding: "8px 16px", borderRadius: "8px", cursor: "pointer", fontSize: "13px", fontWeight: "bold" },
  container: { maxWidth: "800px", margin: "0 auto", padding: "16px" },
  errorBox: { background: "rgba(233,69,96,0.1)", color: "#ff6b6b", padding: "12px", borderRadius: "8px", marginBottom: "16px", border: "1px solid rgba(233,69,96,0.3)" },
  successBox: { background: "rgba(76,175,80,0.1)", color: "#4caf50", padding: "12px", borderRadius: "8px", marginBottom: "16px", border: "1px solid rgba(76,175,80,0.3)" },
  formCard: { background: "var(--bg-card)", borderRadius: "16px", padding: "20px", marginBottom: "20px", border: "1px solid var(--border)" },
  formTitle: { color: "white", fontWeight: "bold", marginBottom: "16px" },
  formGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "14px", marginBottom: "16px" },
  label: { display: "block", color: "var(--text-secondary)", fontSize: "12px", fontWeight: "500", marginBottom: "6px" },
  input: { width: "100%", padding: "11px 13px", borderRadius: "8px", fontSize: "14px", boxSizing: "border-box" },
  preview: { display: "flex", alignItems: "center", gap: "10px", background: "var(--bg-secondary)", padding: "12px 16px", borderRadius: "8px", marginBottom: "14px" },
  previewCode: { background: "var(--bg-primary)", color: "white", padding: "4px 12px", borderRadius: "6px", fontWeight: "bold", fontSize: "13px" },
  previewDiscount: { background: "var(--accent)", color: "white", padding: "4px 12px", borderRadius: "6px", fontWeight: "bold", fontSize: "13px" },
  submitBtn: { width: "100%", padding: "13px", background: "var(--accent)", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "15px", fontWeight: "bold" },
  statsRow: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "10px", marginBottom: "20px" },
  statCard: { background: "var(--bg-card)", borderRadius: "10px", padding: "16px", textAlign: "center", border: "1px solid var(--border)" },
  statNum: { fontSize: "1.6rem", fontWeight: "bold", color: "white", margin: "0 0 4px" },
  statLabel: { color: "var(--text-muted)", fontSize: "12px", margin: 0 },
  emptyState: { textAlign: "center", padding: "40px", background: "var(--bg-card)", borderRadius: "16px", border: "1px solid var(--border)" },
  couponCard: { background: "var(--bg-card)", borderRadius: "12px", padding: "16px", marginBottom: "10px", border: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", flexWrap: "wrap" },
  couponLeft: { display: "flex", gap: "14px", alignItems: "center" },
  codeBadge: { background: "var(--bg-secondary)", borderRadius: "10px", padding: "10px 14px", textAlign: "center", minWidth: "90px", border: "1px solid var(--border)" },
  codeText: { color: "white", fontWeight: "bold", fontSize: "13px", letterSpacing: "1px", margin: "0 0 4px" },
  discountText: { color: "var(--accent)", fontWeight: "bold", fontSize: "12px", margin: 0 },
  couponMeta: { color: "var(--text-muted)", fontSize: "12px", marginBottom: "4px" },
  couponRight: { display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "8px" },
  statusBadge: { padding: "4px 10px", borderRadius: "20px", fontSize: "11px", fontWeight: "bold" },
  toggleBtn: { background: "var(--bg-secondary)", border: "1px solid var(--border)", color: "var(--text-secondary)", padding: "6px 12px", borderRadius: "6px", cursor: "pointer", fontSize: "12px" },
  deleteBtn: { background: "rgba(233,69,96,0.1)", border: "1px solid rgba(233,69,96,0.3)", color: "var(--accent)", padding: "6px 10px", borderRadius: "6px", cursor: "pointer", fontSize: "12px" },
};